import React, { useState, useEffect } from 'react';
import type { ReceiptPrinter } from '../../types';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface PrinterModalProps {
  printer: ReceiptPrinter | null;
  onClose: () => void;
  onSave: (printer: ReceiptPrinter) => void;
}

const PrinterModal: React.FC<PrinterModalProps> = ({ printer, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    enabled: true,
    type: 'browser' as 'browser' | 'lan',
    ipAddress: '',
    port: '9100',
  });
  const [id, setId] = useState<string | null>(null);

  useEffect(() => {
    if (printer) {
      setFormData({ 
        name: printer.name, 
        enabled: printer.enabled,
        type: printer.type || 'browser',
        ipAddress: printer.ipAddress || '',
        port: printer.port ? printer.port.toString() : '9100',
      });
      setId(printer.id);
    } else {
      setFormData({ name: '', enabled: true, type: 'browser', ipAddress: '', port: 9100 });
      setId(null);
    }
  }, [printer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    const nextValue = type === 'checkbox' 
      ? checked 
      : name === 'port' 
        ? value 
        : (type === 'number' ? parseInt(value, 10) || 0 : value);
    setFormData(prev => ({ ...prev, [name]: nextValue }));
  };

  const nameKeyboard = useSoftKeyboardField({
    id: 'printer-modal-name',
    type: 'alphanumeric',
    label: 'Printer Name',
    value: formData.name,
    onChange: (v) => setFormData((prev) => ({ ...prev, name: v })),
  });
  const ipKeyboard = useSoftKeyboardField({
    id: 'printer-modal-ip',
    type: 'alphanumeric',
    label: 'IP Address',
    value: formData.ipAddress,
    onChange: (v) => setFormData((prev) => ({ ...prev, ipAddress: v })),
  });
  const portKeyboard = useSoftKeyboardField({
    id: 'printer-modal-port',
    type: 'numeric',
    label: 'Port',
    value: String(formData.port),
    onChange: (v) => setFormData((prev) => ({ ...prev, port: v })),
    manualValidator: (v) => v === '' || /^\d*$/.test(v),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Please provide a name for the printer.');
      return;
    }
    if (formData.type === 'lan' && !formData.ipAddress) {
      alert('Please provide an IP address for the LAN printer.');
      return;
    }

    const resolvedPort = formData.port ? parseInt(formData.port, 10) : 9100;
    onSave({
      id: id || '',
      name: formData.name,
      enabled: formData.enabled,
      type: formData.type,
      ipAddress: formData.type === 'lan' ? formData.ipAddress : undefined,
      port: formData.type === 'lan' ? (resolvedPort || 9100) : undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">{printer ? 'Edit Printer' : 'Add New Printer'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Printer Name</label>
            <input type="text" name="name" value={formData.name} placeholder="e.g., Kitchen Printer or Bar Printer" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...nameKeyboard} required />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Printer Type</label>
            <select name="type" value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500">
              <option value="browser">Browser Print (Default)</option>
              <option value="lan">LAN Network Printer</option>
            </select>
          </div>
          {formData.type === 'lan' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                <input type="text" name="ipAddress" value={formData.ipAddress} placeholder="192.168.1.100" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...ipKeyboard} required={formData.type === 'lan'} />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
                <input type="number" name="port" value={formData.port} placeholder="9100" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...portKeyboard} required={formData.type === 'lan'} />
                <p className="text-xs text-gray-500 mt-1">Default port for ESC/POS printers is 9100</p>
              </div>
            </>
          )}
          <div className="mb-6">
            <label className="flex items-center">
              <input type="checkbox" name="enabled" checked={formData.enabled} onChange={handleChange} className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500" />
              <span className="ml-2 text-sm text-gray-900">Enabled</span>
            </label>
          </div>
          <div className="flex justify-end space-x-4">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600">Save Printer</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PrinterModal;